import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  updateProfile,
  type User,
  type AuthError,
  type UserCredential,
} from 'firebase/auth'
import { auth, getActiveAuth, googleProvider } from '../config/firebase'
import { logoutCleanup } from '../utils/cacheUtils'
import { loggingService, LogEventType } from './loggingService'

// Input validation utilities
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

const sanitizeInput = (input: string): string => {
  // Remove any potential XSS characters and trim whitespace
  return input.trim().replace(/[<>"'&]/g, '')
}

// Check for in-app browsers that block Google Sign-In
const isInAppBrowser = (): boolean => {
  if (typeof window === 'undefined' || !window.navigator) return false
  
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera || ''
  
  // Common in-app browser identifiers
  // FBAN/FBAV = Facebook
  // Instagram = Instagram
  // LinkedInApp = LinkedIn
  // Twitter = Twitter
  // Snapchat = Snapchat
  // Line = Line
  const inAppRules = [
    /FBAN/,
    /FBAV/,
    /Instagram/,
    /LinkedInApp/,
    /Twitter/,
    /Snapchat/,
    /Line/,
    /Pinterest/,
    /Musical_ly/, // TikTok
    /TikTok/,
  ]
  
  return inAppRules.some(rule => rule.test(ua))
}

const validatePassword = (password: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long')
  }
  
  // More lenient validation for development
  // Just require 8+ characters for now
  
  return { isValid: errors.length === 0, errors }
}

// Custom error types for better error handling
export interface AuthServiceError {
  code: string
  message: string
  originalError?: AuthError
}

// Extended Error type with originalError property
interface ExtendedError extends Error {
  originalError?: AuthError
}

// Auth service class with security best practices
export class AuthService {
  // Sign up with email and password
  static async signUp(
    email: string, 
    password: string, 
    displayName?: string
  ): Promise<UserCredential> {
    try {
      // Validate and sanitize inputs
      const sanitizedEmail = sanitizeInput(email).toLowerCase()
      const sanitizedDisplayName = displayName ? sanitizeInput(displayName) : undefined
      
      if (!validateEmail(sanitizedEmail)) {
        throw new Error('Please enter a valid email address')
      }
      
      const passwordValidation = validatePassword(password)
      if (!passwordValidation.isValid) {
        throw new Error(passwordValidation.errors.join('. '))
      }
      
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        sanitizedEmail, 
        password
      )
      
      // Update display name if provided
      if (sanitizedDisplayName && userCredential.user) {
        await updateProfile(userCredential.user, {
          displayName: sanitizedDisplayName
        })
      }
      
      return userCredential
    } catch (error) {
      const authError = error as AuthError
      throw AuthService.handleAuthError(authError)
    }
  }
  
  // Sign in with email and password
  static async signIn(email: string, password: string): Promise<UserCredential> {
    try {
      // Validate and sanitize inputs
      const sanitizedEmail = sanitizeInput(email).toLowerCase()
      
      if (!validateEmail(sanitizedEmail)) {
        throw new Error('Please enter a valid email address')
      }
      
      if (!password.trim()) {
        throw new Error('Password is required')
      }
      
      const result = await signInWithEmailAndPassword(auth, sanitizedEmail, password)
      
      // Log successful login for audit trail
      try {
        await loggingService.logSecurityEvent(LogEventType.USER_LOGIN, {
          method: 'email_password',
          email: sanitizedEmail
        })
      } catch (logError) {
        console.warn('Failed to log login event:', logError)
        // Don't fail the login if logging fails
      }
      
      return result
    } catch (error) {
      const authError = error as AuthError
      throw AuthService.handleAuthError(authError)
    }
  }
  
  // Sign in with Google
  static async signInWithGoogle(): Promise<UserCredential> {
    try {
      // Check for in-app browsers first to prevent "Access blocked" errors
      if (isInAppBrowser()) {
        throw new Error('Google Sign-In is not supported in this app\'s browser. Please open this page in your device\'s system browser (Safari or Chrome).')
      }

      // Prefer redirect flow when cross-origin isolation or security policies
      // may block popup polling (e.g., COOP/COEP). This avoids
      // "Cross-Origin-Opener-Policy policy would block the window.closed call" errors.
      const forceRedirect =
        (typeof window !== 'undefined' && (window as any).crossOriginIsolated === true) ||
        (import.meta.env.VITE_FORCE_OAUTH_REDIRECT === 'true')

      if (forceRedirect) {
        // Redirect-based auth does not resolve with UserCredential here;
        // onAuthStateChanged will handle state after return.
        await signInWithRedirect(auth, googleProvider)
        // Keep the promise pending to avoid flashing an error before unload
        return new Promise(() => {}) as unknown as UserCredential
      }

      return await signInWithPopup(auth, googleProvider)
    } catch (error) {
      const authError = error as AuthError
      
      // Handle popup closed by user
      if (authError.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in was cancelled')
      }

      // Some browsers/environments emit SecurityError or messages mentioning COOP
      const message = (authError?.message || '').toLowerCase()
      const name = (authError as any)?.name || ''
      const looksLikeCoopBlock =
        message.includes('cross-origin-opener-policy') ||
        message.includes('would block the window.closed') ||
        message.includes('securityerror') ||
        authError.code === 'auth/popup-blocked'

      if (looksLikeCoopBlock || name === 'SecurityError') {
        try {
          // Fallback to redirect flow
          await signInWithRedirect(auth, googleProvider)
          return new Promise(() => {}) as unknown as UserCredential
        } catch (redirectErr) {
          // If redirect also fails, surface original error handling below
        }
      }
      
      throw AuthService.handleAuthError(authError)
    }
  }
  
  // Sign out
  static async signOut(): Promise<void> {
    try {
      // Log logout event for audit trail
      try {
        await loggingService.logSecurityEvent(LogEventType.USER_LOGOUT, {
          method: 'manual_signout'
        })
      } catch (logError) {
        console.warn('Failed to log logout event:', logError)
        // Don't fail the logout if logging fails
      }
      
      logoutCleanup()
      await signOut(auth)
    } catch (error) {
      const authError = error as AuthError
      throw AuthService.handleAuthError(authError)
    }
  }
  
  // Send password reset email
  static async sendPasswordReset(email: string): Promise<void> {
    try {
      const sanitizedEmail = sanitizeInput(email).toLowerCase()
      
      if (!validateEmail(sanitizedEmail)) {
        throw new Error('Please enter a valid email address')
      }
      
      console.log('Sending password reset request to backend...')
      
      // Use backend endpoint to ensure SendGrid templates are used
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.livewello.com'
      const response = await fetch(`${apiUrl}/api/v1/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: sanitizedEmail }),
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Password reset failed' }))
        throw new Error(errorData.detail || 'Password reset failed. Please try again.')
      }
      
      const data = await response.json()
      console.log('✅ Password reset email sent:', data.message)
      
    } catch (error) {
      console.error('❌ Password reset error details:', error)
      
      if (error instanceof Error) {
        throw error
      }
      
      throw new Error('Failed to send password reset email. Please try again.')
    }
  }
  
  // Get current user
  static getCurrentUser(): User | null {
    return auth.currentUser
  }
  
  // Handle Firebase Auth errors with user-friendly messages
  private static handleAuthError(error: AuthError): Error {
    let message: string
    
    switch (error.code) {
      case 'auth/user-not-found':
        message = 'No account found with this email address'
        break
      case 'auth/wrong-password':
        message = 'Incorrect password'
        break
      case 'auth/invalid-credential':
        message = 'Invalid email or password'
        break
      case 'auth/email-already-in-use':
        message = 'An account with this email already exists'
        break
      case 'auth/weak-password':
        message = 'Password is too weak. Please choose a stronger password'
        break
      case 'auth/invalid-email':
        message = 'Please enter a valid email address'
        break
      case 'auth/user-disabled':
        message = 'This account has been disabled'
        break
      case 'auth/too-many-requests':
        message = 'Too many failed attempts. Please try again later'
        break
      case 'auth/network-request-failed':
        message = 'Network error. Please check your connection and try again'
        break
      case 'auth/popup-blocked':
        message = 'Popup was blocked. Please allow popups and try again'
        break
      case 'auth/operation-not-allowed':
        message = 'This sign-in method is not enabled'
        break
      case 'auth/internal-error':
        message = 'Internal server error. Please try again later'
        break
      case 'auth/invalid-api-key':
        message = 'Authentication service configuration error'
        break
      case 'auth/domain-not-allowed':
        message = 'This domain is not authorized for authentication'
        break
      default:
        // Check if it's a network-related error
        if (error.message.includes('network') || error.message.includes('fetch') || error.message.includes('disconnected')) {
          message = 'Network connection error. Please check your internet connection and try again.'
        } else {
          message = error.message || 'An authentication error occurred'
        }
    }
    
    const customError = new Error(message) as ExtendedError
    // Preserve original error for debugging
    customError.originalError = error
    return customError
  }

  // Clear all local storage on logout
  static clearStorage(): void {
    try {
      // Clear auth-related storage
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      
      // Clear app-specific storage
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith('healthRecords_') || 
            key.startsWith('wellnessGoals_') ||
            key.startsWith('documents_') ||
            key.startsWith('profileData_') ||
            key.startsWith('chatThread_')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('Failed to clear localStorage:', error);
    }
  }
}

export default AuthService 
